// Module dependencies
import Logging from 'app/common/lib/Logging';

const logToServerLoader = () =>
  import(/* webpackChunkName: "LogToServerCommon" */ 'app/common/lib/LogToServer');

const ttLogger = Logging.getLogger('TrackingTimer');

/**
 * Maximum time that can elapse for the tracker to send data. Set to 10 min.
 * Values exceeding 6-digits may cause API errors.
 * @type {number}
 */
const MAX_TIME_MS = 10 * 60 * 1000;

/**
 * Only enable the timer if performance.now() is available
 * @type {boolean}
 */
const PERFORMANCE_TIMING_ENABLED = !!(
  typeof window !== 'undefined' &&
  window.performance &&
  window.performance.now
);

/**
 * Simple class to encapsulate all logic dealing with tracking time.
 *
 * @param {string} category A string for categorizing all user timing variables
 *     into logical groups for easier reporting purposes.
 * @param {string} variable A string to indicate the name of the action being tracked.
 * @param {string=} opt_label A string that can be used to add flexibility in
 *     visualizing user timings in the reports.
 * @constructor
 */
export function Timer(category, variable, opt_label) {
  this.category = category;
  this.variable = variable;

  this.label = opt_label;

  /* eslint-disable no-unused-expressions */
  /**
   * startTime should either be null (if performance.now is not availble) or the time
   * elapsed since navigationStart.
   * @type {number|null}
   */
  this.startTime;

  /** @type {number} */
  this.elapsedTime;
  /* eslint-enable */
}

/**
 * Helper method to calculate the time now.
 * @private
 * @return {number}
 */
function _getTime() {
  return PERFORMANCE_TIMING_ENABLED ? window.performance.now() : Date.now();
}

/**
 * Starts the timer.
 * @return {!Timer}
 */
Timer.prototype.start = function() {
  this.startTime = _getTime();
  return this;
};

/**
 * Ends the timer and sets the elapsed time. Drop the decimals, in case we are
 * using performance.now.
 * If we are not using performance.now, null out the startTime.
 * @return {!Timer}
 */
Timer.prototype.end = function() {
  this.elapsedTime = Math.round(_getTime() - this.startTime);
  this.startTime = PERFORMANCE_TIMING_ENABLED ? Math.round(this.startTime) : null;
  return this;
};

/**
 * Returns the elapsed time
 * @return {!Timer}
 */
Timer.prototype.getCompletedTime = function() {
  return this.elapsedTime;
};

/**
 * Send the timing information.
 */
Timer.prototype.send = function() {
  if (this.elapsedTime > 0 && this.elapsedTime < MAX_TIME_MS) {
    const { category, variable, label, elapsedTime } = this;

    logToServerLoader().then(({ default: LogToServer }) => {
      LogToServer.logToServer(
        LogToServer.SERVER_LOG_TYPE.INTERACTION_TIMING,
        LogToServer.HTTP_METHOD.POST,
        {
          category,
          variable,
          label,
          time: elapsedTime,
        },
      );
    });
  }

  return this;
};

/**
 * A hash of timers and utility functions to add/remove from the hash.
 * @constructor
 */
function TrackingTimer() {
  /** {Object.<string, number>} */
  this.timers = {};
}

/**
 * Create a new timer and add to the hash.
 * @param {string} category primary category
 * @param {string} variable subcategory
 * @param {string=} label optional label
 */
TrackingTimer.prototype.start = function(category, variable, label, ...args) {
  const key = Array.prototype.slice.call([category, variable, label, ...args], 0, 2).join(':');

  const timer = new Timer(category, variable, label).start();
  this.timers[key] = timer;
  ttLogger.debug('Starting timer (' + key + ')');
};

/**
 * End the timer identified by the arguments. Timing information can also be
 *     sent to the interaction timing log if `send=true`.
 * @param  {string} category primary category
 * @param  {string} variable subcategory
 * @param  {boolean=} send
 */
TrackingTimer.prototype.end = function(category, variable, send) {
  const key = category + ':' + variable;
  const timer = this.timers[key];

  if (timer) {
    ttLogger.debug('Ending timer (' + key + ')');
    timer.end();

    if (send) {
      timer.send();
    }

    delete this.timers[key];
  } else {
    ttLogger.debug("Timer doesn't exist for: " + key);
  }

  return timer;
};

export default new TrackingTimer();
